import React, { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { FileIcon } from '@primer/octicons-react';

import usePageTitle from '../../../utils/hooks/use_page_title';

import { getModelActivityData } from '../../ActivityLog/activity-utils';

import ModelIcon from '../../common/icons/ModelIcon';
import VersionIcon from '../../common/icons/VersionIcon';

import ClipboardLabel from '../../common/ClipboardLabel/ClipboardLabel';
import HeadingLabel from '../../common/Labels/HeadingLabel';
import Icon from '../../common/icons/Icon';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import SubHeadingLabel from '../../common/Labels/SubHeadingLabel';

import ActivityLog from '../../ActivityLog/ActivityLog';
import ContentTags from '../../ContentTags/ContentTags';
import PageNotFound from '../../pages/PageNotFound';
import VersionHistoryTimeline from '../../VersionHistoryTimeline/VersionHistoryTimeline';

import { truncateText } from '../../../utils/text_utils';

import { ModelCommentsProvider, useCommentContext } from '../../contexts/CommentData/CommentData';
import { ModelDataProvider, useModelData } from '../ModelDataContext';
import ModelAssocPipelines from '../../ModelAssocPipelines';
import { useClientContext } from '../../contexts/ClientData/ClientData';
import { useRedirectOnWorkspaceChange } from '../../../utils/hooks/use_redirect_on_value_change';
import { useUserContext } from '../../contexts/UserData/UserData';

import PageLayout from '../../pages/PageLayout';
import PrimaryContent from '../../pages/layouts/PrimaryContent';
import SecondaryContent from '../../pages/layouts/SecondaryContent';

const ActivityLogWrapper = ({ model }) => {
  const routeParams = useParams();
  const { isAuthEnabled } = useClientContext();
  const { userData } = useUserContext();
  const {
    comments,
    error,
    loading,
    canLoadMoreComments,
    addComment,
    updateComment,
    deleteComment,
    loadMoreComments,
  } = useCommentContext();

  if (!isAuthEnabled) {
    return null;
  }

  const activityData = getModelActivityData({
    comments,
    currentUser: userData,
    modelID: routeParams.id,
    modelVersion: routeParams.version,
    modelCreatedAtDate: model?.createdAt,
  });

  return (
    <ActivityLog
      id="model-activity-log"
      height={480}
      modelID={routeParams.id}
      modelVersion={routeParams.version}
      currentUser={userData}
      commentError={error}
      activityData={activityData}
      onAddComment={addComment}
      onUpdateComment={updateComment}
      onDeleteComment={deleteComment}
      canLoadMoreComments={canLoadMoreComments}
      onLoadMoreComments={loadMoreComments}
      areCommentsLoading={loading}
    />
  )
}

export const ModelDetailPage = ({
  model,
  relatedModels,
  loading,
  error,
  modelHistoryError,
  modelHistoryLoading,
  handleAddTag,
  handleDeleteTag,
}) => {
  const routeParams = useParams();

  usePageTitle(`${model ? model.fileName : 'Model'} | Wallaroo`);

  const modelSha = model?.sha;
  const truncatedSha = useMemo(() => {
    if (!modelSha) {
      return modelSha;
    }
    return truncateText(modelSha, 16)
  }, [modelSha]);

  if (!loading && !error && !model) {
    return <PageNotFound modelID={routeParams.id} modelVersion={routeParams.version} />;
  }

  return (
    <div className="w-full mb-16">
      <div className="bg-brand border-t-2 border-border-brand text-white flex justify-center items-center">
        <div className="px-16 py-8 w-full flex-grow flex flex-col items-start">
          <div className="flex items-center">
            <Icon icon={ModelIcon} size={48} useTailwindSizing />
            <HeadingLabel className="ml-2 mt-1" size="4xl">
              {routeParams?.id}
            </HeadingLabel>
          </div>
          <div className="ml-6 my-2 flex items-center">
            <Icon icon={VersionIcon} size={32} useTailwindSizing />
            <SubHeadingLabel className="ml-2" size="2xl">
              {routeParams?.version}
            </SubHeadingLabel>
          </div>
          {
            !model ? (
              error ? (
                <SubHeadingLabel className="ml-2 mt-2" size="xl">
                  Something went wrong loading this model
                </SubHeadingLabel>
              ) : (
                <div className="ml-2 mt-2 flex flex-row">
                  <SubHeadingLabel size="xl">
                    Loading Model Data
                  </SubHeadingLabel>
                  <LoadingSpinner className="ml-2" animationKey="model-detail-loading-spinner" />
                </div>
              )
            ) : (
              <>
                <div className="ml-6 mt-1 flex-grow flex-wrap flex flex-row items-center">
                  <div className="flex items-center">
                    <Icon icon={FileIcon} size={32} />
                    <SubHeadingLabel className="ml-2" size="xl">
                      {model.fileName}
                    </SubHeadingLabel>
                  </div>
                  {
                    modelSha ? (
                      <ClipboardLabel
                        className="ml-2 mt-1"
                        iconSize={12}
                        labelValue={modelSha}
                        displayLabel={`(${truncatedSha})`}
                        LabelComponent={SubHeadingLabel}
                        showClipboardIconOnHover
                      />
                    ) : null
                  }
                </div>
                <div className="ml-2 mt-4 w-4/5 text-brand">
                  <ContentTags
                    tags={model.modelTags.map(({ tag }) => tag.tag)}
                    onAddTag={handleAddTag}
                    onDeleteTag={handleDeleteTag}
                  />
                </div>
              </>
            )
          }
        </div>
      </div>

      {!error && (
        <PageLayout>
          <PrimaryContent>
            <div className="mb-2">
              <VersionHistoryTimeline
                modelID={routeParams.id}
                timelineTitle={`Version History: ${routeParams.id}`}
                versionHistory={relatedModels}
                versionHistoryError={modelHistoryError}
                areVersionsLoading={modelHistoryLoading}
              />
            </div>
            <ModelAssocPipelines id={model?.id} />
          </PrimaryContent>
          <SecondaryContent>
            <ModelCommentsProvider model={model}>
              <ActivityLogWrapper model={model} />
            </ModelCommentsProvider>
          </SecondaryContent>
        </PageLayout>
      )}
    </div>
  );
};

const ContextualModelDetailPage = () => {
  const {
    model,
    loading,
    error,
    relatedModels,
    historyError,
    historyLoading,
    isAnomalyDetectionEnabled,
    addModelTag,
    deleteModelTag,
  } = useModelData();

  useRedirectOnWorkspaceChange('/models')

  return (
    <ModelDataProvider>
      <ModelDetailPage
        model={model}
        relatedModels={relatedModels}
        loading={loading}
        error={error}
        modelHistoryError={historyError}
        modelHistoryLoading={historyLoading}
        handleAddTag={addModelTag}
        handleDeleteTag={deleteModelTag}
        isAnomalyDetectionEnabled={isAnomalyDetectionEnabled}
      />
    </ModelDataProvider>
  );
}

const WrappedModelDetailPage = () => (
  <ModelDataProvider>
    <ContextualModelDetailPage />
  </ModelDataProvider>
)

export default WrappedModelDetailPage;
